/**
 * HTML Sanitizer Utility
 * Provides safe HTML sanitization using DOMPurify
 *
 * SECURITY: Use this for ALL user-generated or database HTML content
 * to prevent XSS attacks through dangerouslySetInnerHTML
 */

import DOMPurify from 'dompurify';

import { createLogger } from '../logger';

const logger = createLogger('Lib - htmlSanitizer');

/**
 * Configuration for DOMPurify sanitization
 * Allows safe HTML tags while blocking scripts and dangerous attributes
 */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'a',
    'code',
    'pre',
    'span',
    'div',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style'],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Block data: URIs and javascript: URIs
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  FORBID_TAGS: [
    'script',
    'iframe',
    'object',
    'embed',
    'form',
    'input',
    'button',
  ],
  KEEP_CONTENT: true,
  RETURN_TRUSTED_TYPE: false,
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 *
 * @param html - Raw HTML string (potentially unsafe)
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```tsx
 * const safeHtml = sanitizeHtml(company.terms_and_conditions);
 * <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
 * ```
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';

  try {
    return DOMPurify.sanitize(html, SANITIZE_CONFIG);
  } catch (error) {
    logger.error('HTML sanitization failed:', error as Error);
    // Return empty string on error to prevent rendering potentially unsafe content
    return '';
  }
}

/**
 * Sanitizes HTML with strict configuration (no styles, only basic formatting)
 * Use for user comments, descriptions, or less trusted content
 */
export function sanitizeHtmlStrict(html: string | null | undefined): string {
  if (!html) return '';

  const STRICT_CONFIG = {
    ...SANITIZE_CONFIG,
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  };

  try {
    return DOMPurify.sanitize(html, STRICT_CONFIG);
  } catch (error) {
    logger.error('Strict HTML sanitization failed:', error as Error);
    return '';
  }
}

/**
 * Strips all HTML tags and returns plain text
 * Use when you want to display content as text only
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';

  try {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  } catch (error) {
    logger.error('HTML stripping failed:', error as Error);
    return '';
  }
}
